import JwtService from "@/core/services/JwtService";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import store from "@/store";
import { IUser, IUserAuthInfo } from "@/store/user/interfaces/UserInterface";
import { AbilityAction, Role, SignupFrom } from "@/store/auth/enum/AuthEnums";
import { appApiClient, onLogin } from "@/core/plugins/apollo";
import { AuthInterface } from "@/store/auth/interfaces/AuthInterface";
import { mutation } from "@/core/services/ApolloService";
import { setError } from "@/core/services/HandlerService";
import SIGN_IN from "@/graphql/auth/SignIn.graphql";
import SIGN_IN_WITH_FACEBOOK from "@/graphql/auth/SignInWithFacebook.graphql";
import REGISTER from "@/graphql/auth/Register.graphql";
import VERIFY_USER from "@/graphql/auth/VerifyUser.graphql";
import { AbilityInterface } from "@/store/auth/interfaces/AbilityInterface";
import { Status } from "@/store/enums/GeneralEnums";
import { useRouter } from "vue-router";

@Module({
  name: "auth/AuthModule",
  namespaced: true,
  dynamic: true,
  // preserveState: true,
  stateFactory: true,
  store: store,
})
export default class AuthModule extends VuexModule implements IUserAuthInfo {
  error: string | null = null;
  user: IUser = {
    id: "",
    photo: "",
    prefix: "",
    name: "",
    surname: "",
    email: "",
    password: "",
    abilities: [
      {
        action: AbilityAction.read,
        subject: "all",
      },
    ],
    role: Role.seller,
    signupFrom: SignupFrom.web,
    facebookId: "",
    accessFailedCount: 0,
    status: Status.active,
    lastLoginDate: "",
    createdAt: "",
    updatedAt: "",
    createdById: "",
    updatedById: "",
  };
  abilities: [AbilityInterface] = [
    {
      action: AbilityAction.read,
      subject: "all",
    },
  ];
  isAuthenticated = !!JwtService.getToken();

  /**
   * @description Get current user object
   * @returns IUser
   */
  get currentUser(): IUser | null {
    return this.user;
  }

  get currentRole(): Role {
    return this.user.role || Role.seller;
  }

  get ability(): [AbilityInterface] {
    return this.abilities;
  }

  /**
   * @description Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * @description Get authentication errors
   * @returns array
   */
  get getError() {
    return this.error;
  }

  /**
   * @description Get user image
   */
  get userImage(): string {
    // if it has pictured in system use it
    if (this.user && this.user.photo) {
      return `media/avatars/${this.user.photo ? this.user.photo : "blank.png"}`;
    }

    if (this.user && this.user.signupFrom === SignupFrom.facebook) {
      // check if log in with facebook
      // use profile from facebook
      return `https://graph.facebook.com/${this.user.facebookId}/picture?type=large&width=200&height=200`;
    }
    // no profile
    return "media/avatars/blank.png";
  }

  @Mutation
  setError(error) {
    this.error = error;
  }

  @Mutation
  async setAuth(auth: AuthInterface) {
    this.isAuthenticated = true;
    this.user = auth.user;
    this.abilities = auth.user.abilities;
    this.error = null;

    await onLogin(appApiClient, this.user.id, auth);
  }

  @Mutation
  setUser(user) {
    this.user = user;
  }

  @Mutation
  setPassword(password) {
    this.user.password = password;
  }

  @Mutation
  purgeAuth() {
    this.isAuthenticated = false;
    this.user = {} as IUser;
    this.error = null;
    JwtService.destroyToken();
  }

  @Action
  async login(payload) {
    try {
      const data = await mutation(SIGN_IN, { input: { ...payload } });

      await this.setAuth(data.signIn);

      return data.signIn;
    } catch (e) {
      await setError(this, e);
    }

    return null;
  }

  @Action
  async loginWithFacebook(payload) {
    try {
      const data = await mutation(SIGN_IN_WITH_FACEBOOK, {
        input: { ...payload },
      });

      await this.setAuth(data.signInWithFacebook);

      return data.signInWithFacebook;
    } catch (e) {
      await setError(this, e);
    }

    return null;
  }

  @Action
  logout() {
    this.purgeAuth();
  }

  @Action
  async register(payload) {
    try {
      const data = await mutation(REGISTER, { input: { ...payload } });
      await this.setAuth(data.register);

      return data.register;
    } catch (e) {
      await setError(this, e);
    }

    return null;
  }

  @Action
  async forgotPassword(payload) {
    // TODO: implement forget password function
    // try {
    //   await ApiService.post("forgot_password", payload);
    //   this.setError({});
    // } catch (e) {
    //   this.setError(e);
    // }

    console.log(payload);
  }

  @Action
  async checkAbility(abilityAction: AbilityAction): Promise<boolean> {
    const router = useRouter();

    const data = this.ability.find((obj) => {
      return (
        obj.subject === "all" || obj.subject === router.currentRoute.value.name
      );
    });

    if (!data) return false;

    return (
      data.action === AbilityAction.manage || data.action === abilityAction
    );
  }

  @Action
  async canAccess(routerName: string): Promise<boolean> {
    // const router = useRouter();
    // const ca = await this.checkAbility(AbilityAction.read);
    //
    // if (!ca) {
    //   await router.push({ name: "sign-in" });
    // }
    //
    // return ca;

    const data = this.ability.find((obj) => {
      return obj.subject === "all" || obj.subject === routerName;
    });

    if (!data) return false;

    return (
      data.action === AbilityAction.manage || data.action === AbilityAction.read
    );
  }

  @Action
  async canCreate(): Promise<boolean> {
    return await this.checkAbility(AbilityAction.create);
  }

  @Action
  async canUpdate(): Promise<boolean> {
    return await this.checkAbility(AbilityAction.update);
  }

  @Action
  async canDelete(): Promise<boolean> {
    return await this.checkAbility(AbilityAction.delete);
  }

  @Action
  async verifyAuth() {
    if (
      JwtService.getToken() &&
      JwtService.getToken()?.trim() != "" &&
      JwtService.getRefreshToken() &&
      JwtService.getRefreshToken()?.trim() != "" &&
      JwtService.getUid() &&
      JwtService.getUid()?.trim() != ""
    ) {
      if (this.currentUser && this.currentUser.id === JwtService.getUid()) {
        return this.currentUser;
      }

      try {
        const data = await mutation(VERIFY_USER, {
          refreshToken: JwtService.getRefreshToken(),
        });

        if (data.verifyUser) {
          await this.setAuth(data.verifyUser);

          return data.verifyUser.user;
        }

        this.purgeAuth();

        return null;
      } catch (e) {
        await setError(this, e);
        return null;
      }
    } else {
      this.purgeAuth();
      return null;
    }

    // if (JwtService.getToken()) {
    //   ApiService.setHeader();
    //
    //   try {
    //     const user = await ApiService.post("verify_token", payload);
    //     this.setAuth(user.data);
    //   } catch (e) {
    //     this.setError(e);
    //     this.purgeAuth();
    //   }
    // } else {
    //   this.purgeAuth();
    // }
  }
}
